import React, { useMemo, useRef } from 'react';
import ContainerHeader from '../components/ContainerHeader';
import {
  ColumnDirective,
  ColumnsDirective,
  ContextMenu,
  Edit,
  Filter,
  GridComponent,
  Group,
} from '@syncfusion/ej2-react-grids';
import { Inject, Page, Sort, Search, Toolbar } from '@syncfusion/ej2-react-grids';
import { Button } from '@material-ui/core';
import { Box, Grid, Tab, Tabs } from '@mui/material';
import ConfirmationDialog from '../components/ConfirmDialogs/ConfirmationDialog';
import useHfOpsTaskController from './CustomHooks/useHfOpsHook';
import EditHfFormDialog from './components/EditHfFormDialogCtn';
import { dateTemplate } from '../GenTask/components/SharedFormFields';
import AddHfFormDialog from './components/AddHfFormDialogCtn';
import { Add } from '@mui/icons-material';

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

class DebugBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught error:', error, info);
  }
  render() {
    if (this.state.hasError) return <div>Error in grid rendering</div>;
    return this.props.children;
  }
}

const HfOpsPage = props => {
  const pageSettings = useMemo(
    () => ({
      pageSize: 30,
      pageSizes: [5, 10, 20, 30, 40],
    }),
    [],
  );

  const filterSettings = {
    type: 'Excel',
  };

  const {
    confirmationDialogProps,
    handleOpenConfirmationDialog,
    openConfirmationDialog,
    setOpenConfirmationDialog,
    hfOpsData,
    handleTabChange,
    currentTab,
    handleOpeneditDialogClick,
    openEditDialog,
    setOpenEditDialog,
    selectedHfTask,
    handleSuccessfulSubmit,
    handleCloseEditDialog, // NEW: Import the close handler
    openAddDialog,
    handleOpenAddDialogClick,
    handleCloseAddDialog,
    handleDeleteHfTask,
  } = useHfOpsTaskController();

  const toolbarOptions = [{ text: 'Search', tooltipText: 'Search', prefixIcon: 'e-search', id: 'search' }];

  const handleActionBegin = args => {
    if (args.requestType === 'beginEdit' || args.requestType === 'save') {
      // Handle grid actions if needed
    }
  };

  const contextRowRef = useRef(null);

  const contextMenuItems = [
    { text: 'Delete', id: 'deleteHfTask', target: '.e-content', iconCss: 'e-icons e-delete' },
  ];

  // Only show the menu when right-clicking the Edit link button
  const handleContextMenuOpen = args => {
    const target = args.event?.target;
    if (!target || !target.closest('.hf-edit-link')) {
      args.cancel = true;
      return;
    }
    contextRowRef.current = args.rowInfo?.rowData || null; // remember the row
  };

  const handleContextMenuClick = args => {
    if (args.item.id === 'deleteHfTask') {
      const rowData = args.rowInfo?.rowData || contextRowRef.current;
      if (rowData) {
        handleDeleteHfTask(rowData); // opens the confirmation dialog
      }
    }
  };

  function editLinkTemplate(rowProps) {
    return (
      <Button
        key={`${rowProps.id}_site`}
        className="hf-edit-link"   // <-- this was missing
        style={{ color: 'blue', fontWeight: 'bold', width: '100%' }}
        onClick={() => handleOpeneditDialogClick(rowProps)}>
        {rowProps.id}
      </Button>
    );
  }

  return (
    <div className="app-wrapper siteWrapper">
      <Box sx={2}>
        <Grid container alignItems="center" justifyContent="space-between" gap={2}>
          <Grid item xs>
            <ContainerHeader match={props.match} title="H/F Ops & Tasks" />
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={handleOpenAddDialogClick} startIcon={<Add />}>
              Add New Task
            </Button>
          </Grid>
        </Grid>
      </Box>

      <div style={{ height: 'calc(100vh - 140px)', width: '100%', paddingTop: '0.5rem' }}>
        <DebugBoundary>
          <GridComponent
            dataSource={hfOpsData}
            contextMenuItems={contextMenuItems}
            contextMenuOpen={handleContextMenuOpen}
            contextMenuClick={handleContextMenuClick}
            filterSettings={filterSettings}
            allowPaging={true}
            pageSettings={pageSettings}
            allowSorting={true}
            allowTextWrap={true}
            allowResizing={true}
            allowMultiSorting={true}
            allowReordering={true}
            allowFiltering={true}
            style={{
              minHeight: 'auto !important',
            }}
            actionBegin={handleActionBegin}
            height="100%"
            toolbar={toolbarOptions}>
            <ColumnsDirective>
              <ColumnDirective
                field="id"
                headerText="Edit Link"
                width="80"
                textAlign="Left"
                isPrimaryKey={true}
                template={editLinkTemplate}
              />
              <ColumnDirective field="employeeName" headerText="Name" width="100" />
              <ColumnDirective field="selectTask" headerText="Select Task" width="100" />
              <ColumnDirective field="date_Time" headerText="Date & Time" width="100" formatter={dateTemplate} />
              <ColumnDirective field="forestSPNumber" headerText="Forest SP Number" width="100" />
              <ColumnDirective field="machine" headerText="Machine" width="100" />
              <ColumnDirective field="total_M3" headerText="Daily M3" width="80" textAlign="Right" />
              <ColumnDirective field="additionalInformation" headerText="Production Record" width="150" />
              <ColumnDirective field="nonProductiveHours" headerText="Non Productive Hours" width="100" />
              <ColumnDirective field="managerApprovedDowntime" headerText="Manager Approved Downtime" width="100" />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Filter, Group, Search, Toolbar, ContextMenu]} />
          </GridComponent>
        </DebugBoundary>
      </div>

      <ConfirmationDialog
        open={openConfirmationDialog}
        setOpen={setOpenConfirmationDialog}
        confirmationFun={confirmationDialogProps.confirmFnc}
        declineFunction={confirmationDialogProps.cancleFnc}
        confirmationText={confirmationDialogProps.confirmText}
      />

      <EditHfFormDialog
        handleSuccessfulSubmit={handleSuccessfulSubmit}
        open={openEditDialog}
        setOpen={handleCloseEditDialog} // CHANGED: Use handleCloseEditDialog instead of setOpenEditDialog
        selectedHfTask={selectedHfTask}
      />

      <AddHfFormDialog
        handleSuccessfulSubmit={handleSuccessfulSubmit}
        open={openAddDialog}
        setOpen={handleCloseAddDialog} // Use handleCloseAddDialog to close the dialog
      />
    </div>
  );
};

export default HfOpsPage;
